/* eslint-disable @typescript-eslint/no-explicit-any */
import { Field, useField } from "formik";
import React, { useEffect, useState } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import {
  FieldErrorMessage,
  FieldHeader,
  FieldHeaderProps,
  FieldHeaderTitle,
  FieldStateProps,
} from "../";
import { useString } from "../../localization/localizationHelpers";
import { BasicInput } from "../BasicInput/BasicInput";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./CapabilitiesPicker.module.css";

type CapabilitiesPickerProps = Readonly<{
  name: string;
  capabilitiesList: Array<{
    value: string;
    label: string;
    subtitle?: string;
    disableDetails?: boolean;
  }>;
}> &
  FieldHeaderProps &
  FieldStateProps;

type CapabilitiesCheckBoxProps = Readonly<{
  text: string;
  subText?: string;
  name: string;
  capabilityName: string;
  capabilityValue: string;
  detailsName: string;
  disableDetails?: boolean;
  disableDetailsName: string;
}> &
  FieldStateProps;

const CapabilitiesCheckBox = ({
  text,
  subText,
  detailsName,
  name,
  capabilityName,
  capabilityValue,
  disableDetails,
  isSaving,
  disableDetailsName,
  ariaLabel,
}: CapabilitiesCheckBoxProps) => {
  const capabilityDetailsTitle = useString("capabilityDetailsTitle");
  const [field, _meta, helpers] = useField({ name });
  const [_capabilityNameField, _capabilityNameMeta, capabilityNameHelpers] = useField({
    name: capabilityName,
  });
  const [detailsField, _detailsMeta, detailsHelpers] = useField({
    name: detailsName,
  });
  const [_disableDetailsField, _disableDetailsMeta, disableDetailsHelpers] = useField({
    name: disableDetailsName,
  });
  const [checkBoxEnabled, setCheckBoxEnabled] = useState(field.value);

  useEffect(() => {
    helpers.setValue(field.value ?? false);
    capabilityNameHelpers.setValue(capabilityValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = (_event: any) => {
    setCheckBoxEnabled(!checkBoxEnabled);
    capabilityNameHelpers.setValue(capabilityValue);
  };

  const onClickDisableDetails = (_event: any) => {
    disableDetailsHelpers.setValue(true);
    capabilityNameHelpers.setValue(capabilityValue);
  };

  const onTextClick = (_event: any) => {
    if (!detailsField.value) {
      detailsHelpers.setValue("");
    }
  };

  return (
    <Row className={commonStyles.slimContainer}>
      <Column>
        <div className={styles.option}>
          <Field
            type="checkbox"
            name={name}
            data-testid={name}
            onClick={disableDetails ? onClickDisableDetails : onClick}
            disabled={isSaving}
            aria-label={text}
          />
          <div>
            <label>{text}</label>
            {subText && <p className={styles.subtitle}>{subText}</p>}
          </div>
        </div>
        {!disableDetails && checkBoxEnabled && (
          <>
            <Row>
              <Column>
                <FieldHeaderTitle title={capabilityDetailsTitle} isRequiredField={true} />
                <BasicInput
                  name={detailsField.name}
                  onClick={onTextClick}
                  disabled={isSaving}
                  isSaving={isSaving}
                  ariaLabel={ariaLabel}
                />
              </Column>
            </Row>
            <FieldErrorMessage fieldName={detailsName} />
          </>
        )}
      </Column>
    </Row>
  );
};

export const CapabilitiesPicker = (props: CapabilitiesPickerProps) => {
  const { name, capabilitiesList, isSaving } = props;
  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <FieldHeader titleClassName={styles.optionTitle} {...props} />
        {capabilitiesList.map((capability, index) => (
          <CapabilitiesCheckBox
            key={`${name}-${index}`}
            text={capability.label}
            subText={capability.subtitle}
            name={`${name}.${index}.selected`}
            capabilityName={`${name}.${index}.capability`}
            capabilityValue={capability.value}
            detailsName={`${name}.${index}.details`}
            disableDetails={capability.disableDetails}
            disableDetailsName={`${name}.${index}.disableDetails`}
            isSaving={isSaving}
            ariaLabel={capability.label}
          />
        ))}
      </Column>
    </Row>
  );
};
